import * as React from 'react';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, Select, MenuItem, Button, DialogActions, Typography, Box, IconButton, InputAdornment, InputLabel } from '@mui/material';
import { Backdrop, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Pagination from '@mui/material/Pagination';

import PlacaVehiculoInput from './PlacaVehiculoInput';
import PlacaTrailerInput from './PlacaTrailerInput';
import PlacaMotoInput from './PlacaMotoInput';

import SearchIcon from '@mui/icons-material/Search';
import { List, ListItem, ListItemText } from '@mui/material';
import { Grid } from '@mui/material';
import { getApiErrorMessage } from '../../../../utils/apiErrorMessage';
import { getVehicleYears } from '../../../../utils/vehicleYears';

const tipos = ['Automovil', 'Motocicleta', 'Camión', 'Camioneta', 'Trailer', 'Contenedor', 'Trailer + Cabezote', 'Cabezote', 'Volco', 'Otro'];
const marcas = ["Peterbilt", "Daf", "Isuzu", "Foton", "Iveco", "Scania", "Hino", "Freightliner", "Kodia", "International", "ProStar", "Mack", "Kenworth", "Eagle", "Chevrolet", "Renault", "Mazda", "Toyota", "Hyundai", "Kia", "Ford", "Volkswagen", "Nissan", "Honda", "Suzuki", "Jeep", "Subaru", "BMW", "Mercedes-Benz", "Audi", "Peugeot", "Fiat", "Mitsubishi", "Land Rover", "Jaguar", "Volvo", "Mini", "Dodge", "BYD", "JAC", "SsangYong", "Otro"];
const colores = ["Blanco", "Negro", "Gris", "Plata", "Rojo", "Azul", "Verde", "Amarillo", "Marrón", "Beige", "Naranja", "Violeta"];

const API_URL = process.env.REACT_APP_API_URL;

export default function DialogCreateVehiculo({ openDialog, handleCloseDialog, handleUpdateData }) {
  const modelos = getVehicleYears();
  const [placa, setPlaca] = useState("");
  const [tipoVehiculo, setTipoVehiculo] = useState("");
  const [modelo, setModelo] = useState("");
  const [marca, setMarca] = useState("");
  const [color, setColor] = useState("");

  const [searchCliente, setSearchCliente] = useState('');
  const [openDialogSearchCliente, setOpenDialogSearchCliente] = useState(false);

  const [clientesData, setClientesData] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [currentPageCliente, setCurrentPageCliente] = useState(1);
  const [totalPagesCliente, setTotalPagesCliente] = useState(1);
  const [countClientes, setCountClientes] = useState(0);

  const rowsPerPageClientes = 5;

  const cacheClientesRef = React.useRef({});

  const handleChangeSetCliente = (value) => {
    setSelectedCliente(value);
  }

  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef(false);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    placa: false,
    tipoVehiculo: false,
    modelo: false,
    marca: false,
    color: false,
    selectedCliente: false,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchClientes = async () => {
      const key = `${searchCliente}|${currentPageCliente}`;
      const cached = cacheClientesRef.current[key];

      // ⚡ CACHE
      if (cached) {
        setClientesFiltrados(cached.results);
        setCountClientes(cached.count);
        setTotalPagesCliente(Math.ceil(cached.count / rowsPerPageClientes));
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/clientes/`, {
          params: {
            search: searchCliente,
            page: currentPageCliente,
            page_size: rowsPerPageClientes,
          },
        });

        if (!isMounted) return;

        const results = res.data.results || [];
        const total = res.data.count || 0;

        // 🔥 guardar cache
        cacheClientesRef.current[key] = {
          results,
          count: total,
        };

        setClientesFiltrados(results);
        setCountClientes(total);
        setTotalPagesCliente(Math.ceil(total / rowsPerPageClientes));

      } catch (error) {
        console.error('Error al buscar clientes:', error);
      }
    };

    if (openDialogSearchCliente) {
      fetchClientes();
    }

    return () => {
      isMounted = false;
    };
  }, [searchCliente, currentPageCliente, openDialogSearchCliente]);


  useEffect(() => {
    axios.get(`${API_URL}/clientes/`)
      .then(response => {
        setClientesData(response.data);
      })
      .catch(error => {
        console.error('Error al realizar la solicitud:', error);
      });
  }, []);

  const validateFields = () => {
    const errors = {
      placa: placa.trim() === "",
      tipoVehiculo: tipoVehiculo === "",
      modelo: modelo === "",
      marca: marca === "",
      color: color === "",
      selectedCliente: selectedCliente === null,
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos.");
      return;
    }
    submitRef.current = true;
    setLoading(true);

    const updatedData = {
      placa,
      tipo_vehiculo: tipoVehiculo,
      modelo,
      marca,
      color,
      cliente_ids: [selectedCliente]
    };
  
    axios.post(`${API_URL}/historial/`, updatedData)
      .then(response => {
        console.log(response);
        handleUpdateData();
        setLoading(false);
        handleCloseDialog();
      })
      .catch(error => {
        setError(getApiErrorMessage(
          error,
          'No fue posible crear el vehículo.',
          ['placa', 'cliente_ids']
        ));
      })
      .finally(() => {
        submitRef.current = false;
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        sx={{
          '& .MuiPaper-root': {
            width: { xs: '95%', sm: '85%', md: '60%' },
            maxWidth: 'none',
          },
        }}
      >
        <Backdrop
          open={loading}
          style={{ zIndex: 9999, color: '#fff' }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <DialogTitle>Crear la Información del Vehículo</DialogTitle>
        <DialogContent>

          <InputLabel>Cliente</InputLabel>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                fullWidth
                value={
                  clientesData.find(c => c.id === selectedCliente)
                    ? `${clientesData.find(c => c.id === selectedCliente).nombre} ${clientesData.find(c => c.id === selectedCliente).apellidos}`
                    : ''
                }
                placeholder="Selecciona un cliente"
                error={fieldErrors.selectedCliente}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item>
              <IconButton onClick={() => setOpenDialogSearchCliente(true)}>
                <SearchIcon />
              </IconButton>
            </Grid>
          </Grid>

          <InputLabel>Tipo</InputLabel>
          <Select
            fullWidth
            value={tipoVehiculo}
            error={fieldErrors.tipoVehiculo}
            onChange={(e) => setTipoVehiculo(e.target.value)}
          >
            {tipos.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {tipo}
              </MenuItem>
            ))}
          </Select>

          {fieldErrors.tipoVehiculo && <Typography color="error">Este campo es obligatorio</Typography>}
              <InputLabel sx={{ marginTop: 2 }} variant='h4'>Placa del Vehiculo</InputLabel>
              {tipoVehiculo === 'Trailer' || tipoVehiculo === 'Volco' ? (
                <PlacaTrailerInput 
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                  disabled={false}
                />
              ) : tipoVehiculo === 'Motocicleta' ? (
                <PlacaMotoInput 
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                  disabled={false}
                />
              ) : tipoVehiculo === 'Trailer + Cabezote' ? (
                <>
                  <PlacaVehiculoInput 
                    onComplete={(value) => setPlaca(value.toUpperCase())} 
                    error={fieldErrors.placa} 
                  disabled={false}
                  />
                  <PlacaTrailerInput 
                    onComplete={(value) => setPlaca(value.toUpperCase())} 
                    error={fieldErrors.placa} 
                  disabled={false}
                  />
                </>
              ) : (
                <PlacaVehiculoInput 
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                />
              )}

          <InputLabel>Modelo</InputLabel>
          <Select
            fullWidth
            value={modelo}
            error={fieldErrors.modelo}
            onChange={(e) => setModelo(e.target.value)}
          >
            {modelos.map((modelo) => (
              <MenuItem key={modelo} value={modelo}>
                {modelo}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.modelo && <Typography color="error">Este campo es obligatorio</Typography>}

          <InputLabel>Marca</InputLabel>
          <Select
            fullWidth
            value={marca}
            error={fieldErrors.marca}
            onChange={(e) => setMarca(e.target.value)}
          >
            {marcas.map((marca) => (
              <MenuItem key={marca} value={marca}>
                {marca}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.marca && <Typography color="error">Este campo es obligatorio</Typography>}

          <InputLabel>Color</InputLabel>
          <Select
            fullWidth
            value={color}
            error={fieldErrors.color}
            onChange={(e) => setColor(e.target.value)}
          >
            {colores.map((color) => (
              <MenuItem key={color} value={color}>
                {color}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.color && <Typography color="error">Este campo es obligatorio</Typography>}

        </DialogContent>

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: "#d32f2f",
                padding: 1,
                borderRadius: 1,
                width: '95%',
              }}
            >
              <Typography sx={{ color: "white", fontWeight: 'bold', marginRight: 1 }}>
                {error}
              </Typography>
              <IconButton color="inherit" onClick={() => setError("")}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            color='error'
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

        <Dialog 
          open={openDialogSearchCliente} 
          onClose={() => setOpenDialogSearchCliente(false)} 
          fullWidth 
          maxWidth="md"
        >
          <DialogTitle>Buscar Cliente</DialogTitle>

          <DialogContent>
            <TextField
              fullWidth
              value={searchCliente}
              onChange={(e) => {
                setSearchCliente(e.target.value);
                setCurrentPageCliente(1);
              }}
              placeholder="Buscar cliente..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            <List>
              {clientesFiltrados.map((cliente) => (
                <ListItem
                  button
                  key={cliente.id}
                  onClick={() => {
                    setSelectedCliente(cliente.id);
                    setOpenDialogSearchCliente(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: selectedCliente === cliente.id ? '#1976d2' : 'transparent',
                    color: selectedCliente === cliente.id ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: selectedCliente === cliente.id ? '#1565c0' : 'rgb(156, 156, 156)',
                    },
                    transition: '0.2s'
                  }}
                >
                  <ListItemText
                    primary={`${cliente.nombre} ${cliente.apellidos}`}
                    secondary={`${cliente.tipo_documento}: ${cliente.documento}`}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>

          {totalPagesCliente > 1 && (
            <Pagination
              count={totalPagesCliente}
              page={currentPageCliente}
              onChange={(e, value) => setCurrentPageCliente(value)}
              color="primary"
              sx={{ mt: 2, display: 'flex', justifyContent: 'center', pb: 2 }}
            />
          )}
        </Dialog>
    </>
  );
}
